import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import {
  CheckIcon,
  KeyIcon,
  MailIcon,
} from "@heroicons/react/solid";
import {
  Col,
  Row,
  Card,
  Form,
  Button,
  InputGroup,
  Modal,
  Table,
  FormCheck,
  ToastContainer,
} from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import CopyToClipboard from "react-copy-to-clipboard";
import { XCircleIcon } from "@heroicons/react/outline";
import {
  useCheckUserDetailsMutation,
  useUserUpdateFieldMutation,
  useUserDropDownMutation,
} from "Services/services";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { CiEdit } from "react-icons/ci";
import { Toast } from "react-bootstrap";

import { UserOnboardingListTable } from "./Tables";
import { MdOutlineAutoMode } from "react-icons/md";
import TableWrapper from "./TableWrapper";

export const GeneralInfoForm = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");

  const [editDetails, setEditDetails] = useState({});
  const [features, setFeatures] = useState([]);
  const [previewDetails, setPreviewDetails] = useState({});
  const [isValidated, setIsValidated] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showEditDefault, setShowEditDefault] = useState(false);

  const [copied, setCopied] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [crmData] = useUserDropDownMutation();
  const [userDetailUpdates] = useUserUpdateFieldMutation();
  const [verify] = useCheckUserDetailsMutation();
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [isEditPassword, setIsEditPassword] = useState(false);
  const [refreshTable, setRefreshTable] = useState(false);

  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [selectedCrmEditOptions, setSelectedCrmEditOptions] = useState([]);
  const [coordinatedCrm, setCoordinatedCrm] = useState(null);

  const [missingFields, setMissingFields] = useState([]);
  const fieldLabels = {
    name: 'Name',
    phone: 'Phone',
    email: 'Email',
    expiry_date: 'Expiry Date',
    user_currency: 'User Currency',
  };

  useEffect(()=> {
    const requiredFields = [
      'name',
      'phone',
      'email',
      'expiry_date',
      'user_currency',
    ];

    const missing = requiredFields.filter((field) => {
      const value = editDetails?.[field];
      return value === undefined || value === null || String(value).trim() === '';
    });

    setMissingFields(missing);
    setIsValidated(missing.length === 0);
  }, [editDetails])

  useEffect(() => {
    fetchCurrencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleEdit = async (userDetails) => {
    // for changing CRM -> getting all Options
    if (userDetails.user_type_id===4) {
      try {
        const response = await crmData({ user_type: 3 });
        if (response?.data?.statusCode === 200) {
          const options = response.data.data.user_details.map((user) => ({
            value: user.id,
            label: `${user.user_name} (${user.email})`,
          }));

          setSelectedCrmEditOptions([
            ...options,
          ]);
        } else {
          setSelectedCrmEditOptions([]);
          console.error("Failed to fetch user data", response.data);
        }
      } catch (error) {
        setSelectedCrmEditOptions([]);
        console.error("Error fetching user data", error);
      }
    }

    setEditDetails(userDetails);
    setFeatures(userDetails.features);
    setShowEdit(true);
  };

  const [isVerifiedPhone, setisVerifiedPhone] = useState();
  const verifyPhone = (phone) => {
    setEditDetails({...editDetails, phone: phone });
    
    const regex = /^(\+|00)?\d{1,3}[-.\s]?\(?\d{1,4}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/;
    if (phone.length >= 6 && phone.length <= 13 && regex.test(phone)) {
      const postData = { data: phone };
      verify(postData)
        .then((success) => {
          if (success?.data?.statusCode === 200) {
            setisVerifiedPhone(true);
          } else if (success?.data?.statusCode > 300) {
            setisVerifiedPhone(false);
            console.error("Verification failed");
          }
        })
        .catch((error) => {
          setisVerifiedPhone(false);
          console.error("Verification failed", error);
        });
    } else {
      setisVerifiedPhone();
    }
  }

  const [isVerifiedEmail, setisVerifiedEmail] = useState();
  const verifyEmail = (e) => {
    const newEmail = e.target.value;
    setEditDetails({...editDetails, email: newEmail });

    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (regex.test(newEmail)) {
      const postData = { data: newEmail };
      verify(postData)
        .then((success) => {
          if (success?.data?.statusCode === 200) {
            setisVerifiedEmail(true);
          } else if (success?.data?.statusCode > 300) {
            setisVerifiedEmail(false);
            console.error("Verification failed");
          }
        })
        .catch((error) => {
          setisVerifiedEmail(false);
          console.error("Verification failed", error);
        });
    } else {
      setisVerifiedEmail(false);
    }
  };

  const [isPasswordEdited, setIsPasswordEdited] = useState(false);

  const [passwordError, setPasswordError] = useState(false);
  // Update this when the password input is edited
  const handlePasswordChange = (e) => {
    const newValue = e.target.value;
    // Limit length and allow valid characters
    if (newValue.length <= 32) {
      // Validate live feedback
      const isValid = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!%*?&#^]{8,32}$/.test(newValue);
      setPasswordError(!isValid);
      setEditDetails({...editDetails, "password": newValue});
    }
    setIsPasswordEdited(true);
  };

  const generatePassword = () => {
    const length = 12;
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const symbols = "@$!%*?&#^";
  
    const all = lower + upper + numbers + symbols;
  
    let newPassword = "";
    newPassword += lower[Math.floor(Math.random() * lower.length)];
    newPassword += upper[Math.floor(Math.random() * upper.length)];
    newPassword += numbers[Math.floor(Math.random() * numbers.length)];
    newPassword += symbols[Math.floor(Math.random() * symbols.length)];
  
    for (let i = 4; i < length; i++) {
      newPassword += all[Math.floor(Math.random() * all.length)];
    }
    // Shuffle newPassword to remove predictable pattern
    newPassword = newPassword
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("");
  
    setEditDetails({...editDetails, "password": newPassword});
    setPasswordError(false);
    setIsPasswordEdited(true);
  };

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(
        "https://openexchangerates.org/api/currencies.json",
        {
          params: {
            app_id: "YOUR_API_KEY", // Replace with your actual API key
          },
        }
      );
      const currencies = Object.entries(response.data).map(([key, value]) => ({
        value: key,
        label: `${key} - ${value}`,
      }));
      setCurrencyOptions(currencies || []);
    } catch (error) {
      console.error("Error fetching currency data:", error);
    }
  };

  const handlePermissionChange = (index, permissionType) => {
    setFeatures((prev) => {
      const newPermissions = [...prev];
      const currentValue = newPermissions[index][permissionType];

      newPermissions[index] = {
        ...newPermissions[index],
        [permissionType]: !currentValue, // toggle the clicked permission
      };

      return newPermissions;
    });
    // console.log("index, permissionType", index, permissionType);
  };

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleClose = () => {
    setShowEditDefault(false);
    setShowEdit(false);
  };

  const handleCloseBack = () => {
    setShowEditDefault(false);
    setShowEdit(true);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();

    const payload = {
      name: editDetails.name,
      phone: editDetails.phone,
      email: editDetails.email,
      ...(isPasswordEdited && {password: editDetails.password}),
      expiry_date: editDetails.expiry_date,
      user_currency: editDetails.user_currency,
      ...((editDetails.user_type_id===4) && {customer_relation_manager: coordinatedCrm}),
    };

    setPreviewDetails(payload);
    setShowEdit(false);
    setShowEditDefault(true);
  };

  const submitEditForm = (e) => {
    e.preventDefault();

    const editedPostData = {
      ...previewDetails,
      customer_relation_manager: editDetails.customer_relation_manager,
      id: editDetails.id,
      features: features,
      user_type: editDetails.user_type,
    };

    userDetailUpdates(editedPostData)
      .then((res) => {
         // If using RTK Query style response
        if (res.error) {
          console.log("error", res.error);
          setToastMessage(res.error?.data?.message || "Error Updating User details!");
          setToastVariant("danger");
        } else if (res?.data?.statusCode === 200) {
          setShowEdit(false);
          setShowEditDefault(false);
          setEditDetails([]);
          setRefreshTable(!refreshTable);
          setToastMessage(res.data?.message || "User details Updated!");
          setToastVariant("success");
        } else {
          setToastMessage(res.data?.message || "Error Updating User details!");
          setToastVariant("danger");
        }
      })
      .catch((error) => {
        console.log("catch error", error);
        setToastMessage(error?.data?.message || "Error Updating User details!");
        setToastVariant("danger");
      })
      .finally(() => {
        setShowToast(true);
      });  
  };

  return (
    <>
      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg={toastVariant}
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Notification</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
      <div>
        
        <Modal
          backdrop="static"
          keyboard={false}
          as={Modal.Dialog}
          centered
          show={showEdit}
          onHide={handleClose}
          size="lg"
        >
          <Modal.Header>
            <Modal.Title className="h6">Edit User Details</Modal.Title>
            <Button variant="close" aria-label="Close" onClick={handleClose} />
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <TableWrapper>
                  <tbody>
                    <tr>
                      <td className="border-0">Name</td>
                      <td className="border-0">
                        <input
                          required
                          type="text"
                          value={editDetails.name || ""}
                          onChange={(e) => {
                            if (/^[A-Za-z]{0,50}$/.test(e.target.value)){
                              setEditDetails({
                                ...editDetails,
                                name: e.target.value,
                              })
                            }
                          }
                          }
                          title="Enter a valid name with 3–50 alphabetic characters only"
                          className="form-control"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="border-0">Phone Number</td>
                      <td className="border-0">
                        <div className="input-group phone-input-group">
                        <PhoneInput
                              country="IN"
                              value={editDetails.phone || ""}
                              onChange={(value) => {
                                verifyPhone(value);
                              }}
                              title="Please enter a valid phone number in the format '+91 XXXXX-XXXXX'."
                              placeholder="+91 99999-99999"
                              style={{ width: "100%" }}
                            />

                            {isVerifiedPhone === true &&
                              <InputGroup.Text className="phone-input-icon" title="Phone number is available and unique">
                                <CheckIcon width={20} className="text-success" />
                              </InputGroup.Text>
                            }
                            {isVerifiedPhone === false &&
                              <InputGroup.Text className="phone-input-icon" title="Phone number is not available or unique">
                                <XCircleIcon width={20} className="text-danger " />
                              </InputGroup.Text>
                            }
                        </div>
                      </td>
                    </tr>

                    <tr>
                      <td className="border-0">Email</td>
                      <td className="border-0">
                        <div className="input-group">
                          <span className="input-group-text">
                            <MailIcon width={20} />
                          </span>
                          <input
                            type="email"
                            value={editDetails.email || ""}
                            onChange={(e) => {
                              verifyEmail(e);
                            }}
                            placeholder="name@company.com"
                            className="form-control"
                            required
                          />
                          {isVerifiedEmail && (
                            <span className="input-group-text" title="Email is available and unique">
                              <CheckIcon width={20} className="text-success" />
                            </span>
                          )}
                          {isVerifiedEmail === false && (
                            <span className="input-group-text" title="Email is not available or unique">
                              <XCircleIcon width={20} className="text-danger" />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="border-0">Password</td>
                      <td className="border-0">
                        <div className="input-group">
                          {isEditPassword ? (
                            <>
                              <span className="input-group-text">
                                <KeyIcon width={20} />
                              </span>
                              <input
                                required
                                type="password"
                                maxLength={32}
                                placeholder="Password"
                                autoComplete="new-password"
                                title="Password must be 8-32 characters, include uppercase, lowercase, number, and special character."
                                value={editDetails.password}
                                onChange={handlePasswordChange} // Use the new handler here
                                className={`form-control ${
                                  passwordError ? "is-invalid" : ""
                                }`}
                              />
                              <span
                                className="input-group-text"
                                onClick={generatePassword}
                                style={{ cursor: "pointer" }}
                                title="Auto-generate password"
                              >
                                <MdOutlineAutoMode size={20} />
                              </span>
                            </>
                          ) : (
                            <Button
                              variant="light"
                              onClick={() =>
                                setIsEditingPassword(!isEditingPassword)
                              }
                            >
                              <CiEdit size={25} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>

                    <tr>
                      <td className="border-0">Expiry Date</td>
                      <td className="border-0">
                        <input
                          type="date"
                          value={editDetails.expiry_date
                            ? moment(editDetails.expiry_date, "DD/MM/YYYY", true).isValid()
                              ? moment(editDetails.expiry_date, "DD/MM/YYYY").format("YYYY-MM-DD")
                              : moment(editDetails.expiry_date).format("YYYY-MM-DD")
                            : ""}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            const formattedDate =
                              moment(newDate).format("YYYY-MM-DD");
                            setEditDetails({
                              ...editDetails,
                              expiry_date: formattedDate,
                            });
                          }}
                          className="form-control"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="border-0">User Currency</td>
                      <td className="border-0">
                        <div className="form-group mb-0">
                          <Select
                            options={currencyOptions} // Provide your options here
                            placeholder="Select Currency"
                            className="mb-0"
                            isSearchable={true}
                            value={currencyOptions.find(
                              (option) =>
                                String(option.value) === String(editDetails.user_currency)
                            ) || null}
                            onChange={(selectedOption) =>
                              setEditDetails({
                                ...editDetails,
                                user_currency: selectedOption
                                  ? selectedOption.value
                                  : null,
                              })
                            }
                            // Ensure the dropdown appears over the card
                            menuPortalTarget={document.body} // Attach the dropdown to the body
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                borderRadius: '0.75rem',
                              }),
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }), // High z-index
                            }}
                          />
                        </div>
                      </td>
                    </tr>

                    {editDetails.user_type_id === 4 && (
                      <tr>
                        <td className="border-0">CRM Manager</td>
                        <td className="border-0">
                            <Select
                              options={selectedCrmEditOptions}
                              placeholder="Select User"
                              className="mb-0"
                              isSearchable={true}
                              value={selectedCrmEditOptions.find(u => String(u.value) === String(editDetails.customer_relation_manager)) || null}
                              onChange={(selectedOption) => {
                                  setEditDetails({ ...editDetails, customer_relation_manager: selectedOption.value });
                                  setCoordinatedCrm(selectedOption.label)
                                }
                              }
                              menuPortalTarget={document.body}
                              styles={{
                                control: (provided) => ({
                                  ...provided,
                                  borderRadius: '0.75rem',
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                            />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </TableWrapper>
                {missingFields.length > 0 && (
                    <div className="mt-2">
                      <div className="fw-bold mb-1">Please fill the following required fields:</div>
                      <ul className="mb-0">
                        {missingFields.map((f) => (
                          <li key={f}>{fieldLabels[f] || f}</li>
                        ))}
                      </ul>
                    </div>
                  )}
              </Col>
            </Row>
            <Card className="my-3 m-0 mb-0">
            <div className="custom-scroll-wrapper">
              <div className="scroll-container h-smaller">
                <Table
                  hover
                  responsive
                  className="align-items-center table-flush table-outlines"
                >
                <thead className="bg-light sticky">
                  <tr className="bg-gray-100">
                    <th style={{ width: "40%" }}>Features</th>
                    <th className="text-center">Read</th>
                    <th className="text-center">Create/Edit</th>
                    <th className="text-center">Download</th>
                    <th className="text-center">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((perm, index) => (
                    <tr key={index}>
                      <td className="" title={perm.name}>
                        {perm.name}
                      </td>
                      {["read", "edit", "download", "delete"].map((field) => (
                        <td key={field} className="text-center">
                          {perm[field] === undefined || perm[field] === "" ? (
                            "-"
                          ) : (
                            <Form.Check
                              type="checkbox"
                              name={`perm-${perm.name}`} // Changed to use feature name instead of index
                              checked={perm[field]}
                              onChange={() =>
                                handlePermissionChange(index, field)
                              }
                              className="d-inline-block"
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>
              </div>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleEditSubmit}
              disabled={!isValidated || (isEditPassword && (!!passwordError || !editDetails.password))}>
              Preview
            </Button>
          </Modal.Footer>
        </Modal>

{/* password confirmation */}
        <Modal
          backdrop="static"
          keyboard={false}
          as={Modal.Dialog}
          centered
          show={isEditingPassword && !isEditPassword}
          onHide={handleClose}
        >
          <Modal.Header>
            <Modal.Title className="h6">Edit Password Confirmation</Modal.Title>
            <Button
              variant="close"
              aria-label="Close"
              onClick={() => setIsEditingPassword(!isEditingPassword)}
            />
          </Modal.Header>
          <Modal.Body>
            <p>Once confirmed, you will be required to update your password.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setIsEditPassword(!isEditPassword);
                setEditDetails((prevDetails) => ({
                  ...prevDetails,
                  password: "",
                }));
              }}
            >
              Confirm
            </Button>
            <Button
              variant="link"
              className="text-gray ms-auto"
              onClick={() => setIsEditingPassword(!isEditingPassword)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* preview modal */}
        <Modal
          backdrop="static"
          keyboard={false}
          as={Modal.Dialog}
          centered
          show={showEditDefault}
          onHide={handleCloseBack}
          size="lg"
        >
          <Modal.Header>
            <Modal.Title className="h6">
              Editted User Detail Preview
            </Modal.Title>
            <Button
              variant="close"
              aria-label="Close"
              onClick={handleCloseBack}
            />
          </Modal.Header>
          <Modal.Body>
            <Card className="mb-2 m-0">
              <Row>
                <Col md={10}>
              
                  <TableWrapper>
                    <tbody>
                      <tr>
                        <td className="border-0">Name</td>
                        <td className="border-0" title={previewDetails.name}>{previewDetails.name}</td>
                      </tr>
                      <tr>
                        <td className="border-0">Phone Number</td>
                        <td className="border-0" title={previewDetails.phone}>{previewDetails.phone}</td>
                      </tr>
                      <tr>
                        <td className="border-0">Email</td>
                        <td className="border-0" title={previewDetails.email}>{previewDetails.email}</td>
                      </tr>
                      {isPasswordEdited && previewDetails.password !== undefined && (
                        <tr>
                          <td className="border-0">Password</td>
                          <td className="border-0" title={previewDetails.password}>********</td>
                        </tr>
                      )}
                      <tr>
                        <td className="border-0">Expiry Date</td>
                        <td className="border-0">
                          {previewDetails.expiry_date
                            ? moment(previewDetails.expiry_date).format(
                                "DD/MM/YYYY"
                              )
                            : ""}
                        </td>
                      </tr>
                      {coordinatedCrm && (
                        <tr>
                          <td className="border-0">Coordinated CRM</td>
                          <td className="border-0">
                            {previewDetails.customer_relation_manager}
                          </td>
                        </tr>
                      )}

                      <tr>
                        <td className="border-0">User Currency</td>
                        <td className="border-0">
                          {previewDetails.user_currency}
                        </td>
                      </tr>
                    </tbody>
                  </TableWrapper>
                </Col>
                <Col md={2} className="d-flex align-items-start">
                  <Card.Body className="d-flex flex-column">
                    <div>
                      {copied ? (
                        <Button
                          size="sm"
                          variant="success"
                          className="copy-code-button"
                        >
                          Copied
                        </Button>
                      ) : (
                        <CopyToClipboard
                        text={Object.entries(previewDetails)
                          .map(([key, value]) => `${key}: ${value}`)
                          .join("\n")}
                          onCopy={handleCopy}
                        >
                          <Button
                            size="sm"
                            variant="secondary"
                            className="copy-code-button"
                          >
                            Copy
                          </Button>
                        </CopyToClipboard>
                      )}
                    </div>
                  </Card.Body>
                </Col>
              </Row>
            </Card>

            <Card className="my-3 m-0">
                <div className="custom-scroll-wrapper">
              <div className="scroll-container h-smaller">
                <Table
                  hover
                  responsive
                  className="align-items-center table-flush table-outlines"
                >
                <thead className="bg-light sticky">
                  <tr className="bg-gray-50">
                    <th className="">Features</th>
                    <th className="text-center">Read</th>
                    <th className="text-center">Create/Edit</th>
                    <th className="text-center">Download</th>
                    <th className="text-center">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((perm, index) => (
                    <tr key={index}>
                      <td className="" title={perm.name}>{perm.name}</td>
                      <td className="text-center">{perm.read ? "☑️" : "✖️"}</td>
                      <td className="text-center">{perm.edit ? "☑️" : "✖️"}</td>
                      <td className="text-center">
                        {perm.download ? "☑️" : "✖️"}
                      </td>
                      <td className="text-center">
                        {perm.delete ? "☑️" : "✖️"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>
              </div>
            </Card>

            <Card className="mt-3 mx-0 mb-0">
              <Card.Body className="py-2">
                <b>Share Details:</b>
                <FormCheck type="switch">
                  <FormCheck.Input
                    type="checkbox"
                    id="emailSwitch"
                    checked={selectedOption === "email"}
                    onChange={() => setSelectedOption("email")}
                  />
                  <FormCheck.Label
                    htmlFor="emailSwitch"
                    style={{ marginLeft: "8px" }}
                  >
                    Email ID
                  </FormCheck.Label>
                </FormCheck>
                <FormCheck type="switch">
                  <FormCheck.Input
                    type="checkbox"
                    id="phoneSwitch"
                    checked={selectedOption === "phone"}
                    onChange={() => setSelectedOption("phone")}
                  />
                  <FormCheck.Label
                    htmlFor="phoneSwitch"
                    style={{ marginLeft: "8px" }}
                  >
                    Phone Number
                  </FormCheck.Label>
                </FormCheck>
                <FormCheck type="switch">
                  <FormCheck.Input
                    type="checkbox"
                    id="bothSwitch"
                    checked={selectedOption === "both"}
                    onChange={() => setSelectedOption("both")}
                  />
                  <FormCheck.Label
                    htmlFor="bothSwitch"
                    style={{ marginLeft: "8px" }}
                  >
                    Both
                  </FormCheck.Label>
                </FormCheck>
                <FormCheck type="switch">
                  <FormCheck.Input
                    type="checkbox"
                    id="noneSwitch"
                    checked={selectedOption === ""}
                    onChange={() => setSelectedOption("")}
                  />
                  <FormCheck.Label
                    htmlFor="noneSwitch"
                    style={{ marginLeft: "8px" }}
                  >
                    None
                  </FormCheck.Label>
                </FormCheck>
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={(e) => submitEditForm(e)}>
              Submit
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <UserOnboardingListTable key={refreshTable} handleEdit={handleEdit} />
    </>
  );
};
